import {memo} from 'react';
import Image from 'next/image';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {borderRadius, colors, fontSize, fontWeight, lineHeight, shadows, typography} from '@/styles/tokens';
import {nftButtonSx} from '@/styles/nftButtonSx';

interface RnFinalCtaProps {
    heading: string;
    subheading: string;
    buttonText: string;
    openForm: () => void;
    /** Cut-out artwork (transparent background) that rises above the card's top edge. */
    image: string;
    imageAlt: string;
    imageWidth: number;
    imageHeight: number;
}

// Room above the card for the phone to rise into, so it never overlaps the section above.
const wrapSx = {pt: {xs: 0, md: 6}} as const;

// Navy card lit from behind the artwork. Not `overflow: hidden`: the phone breaks out
// of the top edge.
const cardSx = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    minHeight: {md: 300, lg: 320},
    borderRadius: borderRadius['3xl'],
    background: `radial-gradient(ellipse at 70% 50%, ${colors.primary.main} 0%, ${colors.accent.navyDeep} 55%, ${colors.primary.deepMidnight} 100%)`,
    boxShadow: shadows.bottomDrop
} as const;

const contentSx = {
    position: 'relative',
    zIndex: 1,
    px: {xs: 3, md: 5},
    py: {xs: 4, md: 5},
    maxWidth: {xs: '100%', md: '58%'}
} as const;

const headingSx = {
    fontSize: {xs: fontSize[28], md: fontSize[36]},
    lineHeight: {xs: lineHeight[40], md: lineHeight[48]},
    fontWeight: fontWeight.bold,
    color: colors.text.inverse,
    mb: 2
} as const;

const subheadingSx = {...typography.body, color: colors.text.inverse} as const;

const buttonSx = {...nftButtonSx, mt: 4} as const;

// Pinned to the card's bottom-right and taller than the card, so the phone pops out
// above it. Dropped on phones, where the copy takes the full card.
const imageWrapSx = {
    display: {xs: 'none', md: 'block'},
    position: 'absolute',
    right: {md: 16, lg: 48},
    bottom: 0,
    width: {md: 320, lg: 380}
} as const;

const imageStyle = {width: '100%', height: 'auto', display: 'block'} as const;

const RnFinalCta = ({heading, subheading, buttonText, openForm, image, imageAlt, imageWidth, imageHeight}: RnFinalCtaProps) => (
    <MUIContainer>
        <Box sx={wrapSx}>
            <Box sx={cardSx}>
                <Box sx={contentSx}>
                    <Typography component="p" className="font-headers" sx={headingSx}>
                        {heading}
                    </Typography>
                    <Typography component="p" className="font-regular-text" sx={subheadingSx}>
                        {subheading}
                    </Typography>
                    <MUIButton
                        label={buttonText}
                        variant="contained"
                        size="medium"
                        endIcon={<ArrowForwardIcon />}
                        disableRipple={true}
                        className="animated-button moving-icon-button font-headers"
                        sx={buttonSx}
                        onClick={openForm}
                    />
                </Box>
                <Box sx={imageWrapSx}>
                    <Image
                        src={image}
                        alt={imageAlt}
                        width={imageWidth}
                        height={imageHeight}
                        sizes="(max-width: 900px) 0px, 380px"
                        quality={90}
                        style={imageStyle}
                    />
                </Box>
            </Box>
        </Box>
    </MUIContainer>
);

export default memo(RnFinalCta);
